import { getAuthConfig } from './config';
import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
} from './pkce';
import { clearTokens, decodeJwt, parseTokenResponse, setTokens } from './token';
import type { JwtPayload, TokenSet } from './types';

const STORAGE_KEY_VERIFIER = 'auth_pkce_verifier';
const STORAGE_KEY_STATE = 'auth_state';
const STORAGE_KEY_RETURN_URL = 'auth_return_url';
const STORAGE_KEY_SILENT = 'auth_silent_redirect';

export async function initiateLogin(returnUrl?: string): Promise<void> {
  await buildAndRedirect({ returnUrl });
}

/**
 * Silent round-trip redirect: sends the user to Cognito with prompt=none.
 * If the SSO session (Cognito/Okta) is still alive the user returns to
 * /auth/callback with a fresh code — no password prompt.
 * On failure Cognito returns error=login_required and the callback page
 * falls back to the normal /login page.
 */
export async function initiateSilentRedirect(
  returnUrl?: string
): Promise<void> {
  sessionStorage.setItem(STORAGE_KEY_SILENT, 'true');
  await buildAndRedirect({ prompt: 'none', returnUrl });
}

async function buildAndRedirect({
  prompt,
  returnUrl,
}: {
  prompt?: string;
  returnUrl?: string;
}): Promise<void> {
  const config = getAuthConfig();
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateState();

  sessionStorage.setItem(STORAGE_KEY_VERIFIER, verifier);
  sessionStorage.setItem(STORAGE_KEY_STATE, state);
  if (returnUrl) {
    sessionStorage.setItem(STORAGE_KEY_RETURN_URL, returnUrl);
  }

  const params = new URLSearchParams({
    client_id: config.clientId,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    redirect_uri: config.redirectUri,
    response_type: 'code',
    state,
    ...(prompt ? { prompt } : {}),
  });

  window.location.href = `${config.cognitoDomain}/oauth2/authorize?${params.toString()}`;
}

export async function handleCallback(searchParams: URLSearchParams): Promise<{
  isSilent: boolean;
  returnUrl: string | null;
  tokens: TokenSet;
  user: JwtPayload;
}> {
  const isSilent = sessionStorage.getItem(STORAGE_KEY_SILENT) === 'true';
  sessionStorage.removeItem(STORAGE_KEY_SILENT);

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  if (error) {
    throw Object.assign(
      new Error(`Auth error: ${error} - ${errorDescription}`),
      {
        isSilent,
      }
    );
  }

  if (!code) {
    throw Object.assign(new Error('No authorization code in callback URL'), {
      isSilent,
    });
  }

  const storedState = sessionStorage.getItem(STORAGE_KEY_STATE);
  if (!storedState) {
    throw Object.assign(new Error('No auth session found. Please sign in.'), {
      isSilent: true,
    });
  }
  if (storedState !== state) {
    throw new Error('Invalid state parameter - possible CSRF attack');
  }

  const verifier = sessionStorage.getItem(STORAGE_KEY_VERIFIER);
  if (!verifier) {
    throw Object.assign(new Error('Missing PKCE code_verifier'), {
      isSilent: true,
    });
  }

  const returnUrl = sessionStorage.getItem(STORAGE_KEY_RETURN_URL);

  sessionStorage.removeItem(STORAGE_KEY_VERIFIER);
  sessionStorage.removeItem(STORAGE_KEY_STATE);
  sessionStorage.removeItem(STORAGE_KEY_RETURN_URL);

  const config = getAuthConfig();
  const tokenResponse = await exchangeCodeForTokens(
    code,
    verifier,
    config.redirectUri
  );
  const tokens = parseTokenResponse(tokenResponse);
  const user = decodeJwt(tokens.idToken);

  setTokens(tokens);

  return { isSilent, returnUrl, tokens, user };
}

async function exchangeCodeForTokens(
  code: string,
  codeVerifier: string,
  redirectUri: string
): Promise<{
  access_token: string;
  expires_in: number;
  id_token: string;
  refresh_token?: string;
  token_type: string;
}> {
  const config = getAuthConfig();

  const body = new URLSearchParams({
    client_id: config.clientId,
    code,
    code_verifier: codeVerifier,
    grant_type: 'authorization_code',
    redirect_uri: redirectUri,
  });

  const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    body: body.toString(),
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    method: 'POST',
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Token exchange failed: ${response.status} - ${errorBody}`);
  }

  return response.json();
}

export function logout(): void {
  clearTokens();
  const config = getAuthConfig();

  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.logoutRedirectUri,
  });

  window.location.href = `${config.cognitoDomain}/logout?${params.toString()}`;
}
